import {makeObservable, observable} from "mobx";
import {observer} from "mobx-react";
import {Component} from "preact";
import {
	AlertType,
	availabilityModel,
	connection,
	fancyAlert,
	InsufficientStorageError,
	LockStatus,
	occupancyModel,
	OccupancyResponse,
	openAndKeep,
	openAndRelease,
	uiModel,
} from "../model";
import {resources} from "../resources";
import {ChangeLockerDialog} from "./ChangeLockerDialog";
import {Button} from "./controls/Button";
import {Icon} from "./controls/Icon";
import {GracePeriodCountDown} from "./GracePeriodCountDown";
import {HelpDialog} from "./HelpDialog";
import {LockerState, LockerView} from "./LockerView";
import style from "./OccupancyView.module.css";
import {OpenLockerDialog} from "./OpenLockerDialog";

interface Props {
	occupancy: OccupancyResponse;
}

export const OccupancyView = observer(
	class OccupancyView extends Component<Props> {
		private openAndReleasePending = false;
		private openAndKeepPending = false;
		private changeLockerPending: string | false = false;
		private openPending = false;

		constructor(props: Props) {
			super(props);
			makeObservable<
				OccupancyView,
				"openAndReleasePending" | "openAndKeepPending" | "changeLockerPending" | "openPending"
			>(this, {
				openAndReleasePending: observable,
				openAndKeepPending: observable,
				changeLockerPending: observable,
				openPending: observable,
			});
		}

		render() {
			const {occupancy} = this.props;
			const neverOpened = !occupancy.openedTimes;
			const released = !!occupancy.releaseAt;
			const isOpen = occupancy.box.lockStatus == LockStatus.Open;
			const canChangeLocker = connection.online
				&& (neverOpened || released || (isOpen && occupancy.openedTimes == 1));
			const canOpen = connection.online /* && (released || occupancy.box.lockStatus != LockStatus.Open )*/;
			const showCloseMessage = !released && isOpen;
			const cube = occupancy.box.cube;

			return (
				<div class={style.occupancyView}>
					<button className={style.helpButton} onClick={this.handleHelp}>
						<span className={style.helpText}>{resources.strings.Help}</span>
						<Icon>help_outline</Icon>
					</button>

					<div className={style.cubeLocation}>
						<div className={style.siteName}>{cube.site.name}</div>
						<div className={style.cubeName}>{cube.name}</div>
					</div>

					<div class={style.boxSection}>
						<LockerView
							number={occupancy.box.name}
							label={resources.strings.YourLocker}
							lockerState={this.getLockerState()}
						/>

						{canOpen && (
							<Button
								primary={!neverOpened}
								success={neverOpened}
								pending={this.openPending}
								disabled={this.openPending}
								onClick={(neverOpened || released) ? this.handleOpen : this.handleOpenLockerClick}
							>
								{resources.strings.Open}
							</Button>
						)}

						{canOpen && (
							<div class={style.accessCodeSection}>
								<div className={style.accessCodeMessage}>{resources.strings.AccessCode}</div>
								<div className={style.accessCodeOnline}>{occupancy.accessCode}</div>
							</div>
						)}

						{connection.offline && (
							<div class={style.accessCodeSection}>
								<div class={style.accessCodeMessage}>{resources.strings.AccessCodeMessage}</div>
								<div class={style.accessCode}>{occupancy.accessCode}</div>
							</div>
						)}

						{showCloseMessage && (
							<div class={style.closeMessage}>
								{resources.strings.CloseMessage}
							</div>
						)}
					</div>

					{released && (
						<div class={style.gracePeriod}>
							<GracePeriodCountDown occupancy={occupancy} />
						</div>
					)}

					{canChangeLocker && (
						<div class={style.changeLocker}>
							<Button onClick={this.handleOpenChangeLockerClick}>{resources.strings.ChangeLocker}</Button>
						</div>
					)}

					<ChangeLockerDialog
						open={uiModel.changeLocker}
						onClose={this.handleChangeLockerClose}
						boxes={availabilityModel.availability.get(occupancy.box.cube.hostname)}
						pending={this.changeLockerPending}
						onChangeLocker={this.handleChangeLocker}
					/>

					<OpenLockerDialog
						open={uiModel.openLocker}
						onClose={this.handleOpenLockerClose}
						onOpenAndKeep={this.handleOpenAndKeep}
						onOpenAndRelease={this.handleOpenAndRelease}
						openAndKeepPending={this.openAndKeepPending}
						openAndReleasePending={this.openAndReleasePending}
						occupancy={occupancy}
					/>

					<HelpDialog
						open={uiModel.help}
						onClose={this.handleHelpClose}
						contactPhone={occupancy.box.cube.site.contactPhone}
						contactUrl={occupancy.box.cube.site.contactUrl}
						termsUrl={occupancy.box.cube.site.termsUrl}
					/>
				</div>
			);
		}

		getLockerState() {
			if (connection.offline) {
				return LockerState.Offline;
			}
			else if (this.props.occupancy.releaseAt) {
				return LockerState.Released;
			}
			else {
				switch (this.props.occupancy.box.lockStatus) {
					case LockStatus.Open:
						return LockerState.Open;
					case LockStatus.Closed:
						return LockerState.Closed;
				}
			}
			return undefined;
		}

		private handleHelp = () => uiModel.openHelp();
		private handleHelpClose = () => uiModel.back();

		private handleOpen = async () => {
			try {
				this.openPending = true;
				await openAndKeep(this.props.occupancy);
			}
			catch (error) {
				fancyAlert(resources.strings.OpenAndKeepFailedMessage);
			}
			finally {
				this.openPending = false;
			}
		};

		private handleOpenAndRelease = async () => {
			try {
				this.openAndReleasePending = true;
				await openAndRelease(this.props.occupancy);
				uiModel.back();
			}
			catch (error) {
				fancyAlert(resources.strings.OpenAndReleaseFailedMessage);
			}
			finally {
				this.openAndReleasePending = false;
			}
		};

		private handleOpenAndKeep = async () => {
			try {
				this.openAndKeepPending = true;
				await openAndKeep(this.props.occupancy);
				uiModel.back();
			}
			catch (error) {
				fancyAlert(resources.strings.OpenAndKeepFailedMessage);
			}
			finally {
				this.openAndKeepPending = false;
			}
		};

		private handleOpenChangeLockerClick = () => {
			availabilityModel.fetchAvailability(this.props.occupancy.box.cube.hostname);
			uiModel.openChangeLocker();
		};

		private handleOpenLockerClick = () => uiModel.openOpenLocker();

		private handleOpenLockerClose = () => {
			uiModel.back();
			occupancyModel.fetch();
		};

		private handleChangeLockerClose = () => {
			uiModel.back();
			occupancyModel.fetch();
		};

		private handleChangeLocker = async (size: string) => {
			try {
				console.info("Change locker to size", size);
				this.changeLockerPending = size;
				await occupancyModel.changeLocker(this.props.occupancy, size);
			}
			catch (error: any) {
				console.error(error);
				const isInsufficientStorage = error instanceof InsufficientStorageError;
				if (error.code === 507 || isInsufficientStorage) {
					fancyAlert(resources.strings.InsufficientStorageMessage, AlertType.Error);
				}
				else {
					fancyAlert(
						resources.strings.ChangeLockerFailedMessage,
						AlertType.Error,
						resources.strings.ChangeLockerFailedTitle,
					);
				}
			}
			finally {
				this.changeLockerPending = false;
				await occupancyModel.fetch(); // refresh list
			}
		};
	},
);
